import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Home,
  Package,
  Beer,
  Truck,
  DollarSign,
  BarChart3,
  LogOut,
  Menu,
  X,
  Beaker,
  ShoppingBag,
  Leaf,
  FlaskConical,
  BookOpen,
  Droplets,
  Users,
  Share2,
  Thermometer,
  GitBranch,
  Brain,
  QrCode,
  CreditCard,
  ChevronDown,
  Store,
  Settings,
  PanelLeftClose,
  PanelLeft,
  ClipboardCheck,
  Sprout,
  Ticket,
  Map,
  Crown,
  Award,
  ShoppingCart,
  CalendarDays,
  Banknote,
  Send,
  Search,
  Monitor,
  Globe,
  Receipt,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { OrgSwitcher } from './OrgSwitcher';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

const navigation: NavEntry[] = [
  { name: 'Dashboard', href: '/', icon: Home },
  {
    label: 'Brewing',
    icon: BookOpen,
    items: [
      { name: 'Recipes', href: '/recipes', icon: BookOpen },
      { name: 'Ingredients', href: '/ingredients', icon: FlaskConical },
      { name: 'Hops Database', href: '/hops', icon: Leaf },
      { name: 'Water Profiles', href: '/water', icon: Droplets },
    ],
  },
  {
    label: 'Production',
    icon: Beer,
    items: [
      { name: 'Contract Brewing', href: '/contract-brewing', icon: Beer },
      { name: 'Inventory', href: '/inventory', icon: Package },
      { name: 'Keg Management', href: '/kegs', icon: Beer },
      { name: 'QR Tracking', href: '/qr-tracking', icon: QrCode },
    ],
  },
  {
    label: 'Distribution',
    icon: Truck,
    items: [
      { name: 'Deliveries', href: '/distribution', icon: Truck },
      { name: 'DTC Shipping', href: '/dtc-shipping', icon: Send },
      { name: 'Customers', href: '/distribution/customers', icon: Users },
      { name: 'Driver Tools', href: '/distribution/drivers', icon: ClipboardCheck },
      { name: 'Zones & Recurring', href: '/distribution/zones', icon: Map },
    ],
  },
  {
    label: 'Sales',
    icon: DollarSign,
    items: [
      { name: 'Products', href: '/products', icon: DollarSign },
      { name: 'OTA Brew Pub', href: '/ota', icon: Store },
      { name: 'Merchandise', href: '/merchandise', icon: ShoppingBag },
      { name: 'Beer Club', href: '/beer-club', icon: Crown },
      { name: 'Loyalty Program', href: '/loyalty', icon: Award },
      { name: 'Beer Finder', href: '/beer-finder', icon: Search },
      { name: 'Taproom POS', href: '/pos', icon: Receipt },
      { name: 'Menu Boards', href: '/menu-boards', icon: Monitor },
      { name: 'Website Builder', href: '/website-builder', icon: Globe },
      { name: 'Billing', href: '/billing', icon: CreditCard },
    ],
  },
  {
    label: 'Lab',
    icon: Beaker,
    items: [
      { name: 'R&D Center', href: '/rnd', icon: Beaker },
      { name: 'AI Assistant', href: '/ai', icon: Brain },
    ],
  },
  {
    label: 'Operations',
    icon: BarChart3,
    items: [
      { name: 'IoT Monitoring', href: '/monitoring', icon: Thermometer },
      { name: 'Cold Chain', href: '/monitoring/cold-chain', icon: Thermometer },
      { name: 'Workflows', href: '/workflows', icon: GitBranch },
      { name: 'Analytics', href: '/analytics', icon: BarChart3 },
      { name: 'Scheduling', href: '/scheduling', icon: CalendarDays },
      { name: 'Tip Management', href: '/tips', icon: Banknote },
      { name: 'Sustainability', href: '/sustainability', icon: Sprout },
    ],
  },
  {
    label: 'Community',
    icon: Users,
    items: [
      { name: 'Community Hub', href: '/community', icon: Users },
      { name: 'Collaboration', href: '/collaboration', icon: Share2 },
      { name: 'Events & Tours', href: '/events', icon: Ticket },
      { name: 'B2B Marketplace', href: '/marketplace', icon: ShoppingCart },
    ],
  },
  { name: 'Settings', href: '/settings', icon: Settings },
];

function SidebarGroup({
  group,
  isActive,
  onNavigate,
  collapsed,
}: {
  group: NavGroup;
  isActive: (path: string) => boolean;
  onNavigate: () => void;
  collapsed: boolean;
}) {
  const groupActive = group.items.some((item) => isActive(item.href));
  const [expanded, setExpanded] = useState(groupActive);
  const Icon = group.icon;
  const location = useLocation();

  useEffect(() => {
    if (group.items.some((item) => isActive(item.href))) {
      setExpanded(true);
    }
  }, [location.pathname]);

  if (collapsed) {
    return (
      <div className="relative group/nav">
        <div
          className={`
            flex items-center justify-center p-2 rounded-lg transition-colors cursor-pointer
            ${groupActive ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}
          `}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="absolute left-full top-0 ml-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 hidden group-hover/nav:block z-50">
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {group.label}
          </div>
          {group.items.map((item) => {
            const ItemIcon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                className={`
                  flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
                  ${active
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <ItemIcon className="w-4 h-4 flex-shrink-0" />
                {item.name}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`
          w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
          ${groupActive ? 'text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
        `}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-gray-200 pl-3">
          {group.items.map((item) => {
            const ItemIcon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                className={`
                  flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors
                  ${active
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <ItemIcon className="w-4 h-4 flex-shrink-0" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const SIDEBAR_KEY = 'brewflow-sidebar-collapsed';

export function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(collapsed)); } catch {}
  }, [collapsed]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const sidebarWidth = collapsed ? 'w-16' : 'w-60';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 flex flex-col
          transition-all duration-200 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:z-auto lg:flex-shrink-0
          ${mobileOpen ? 'w-60' : sidebarWidth}
        `}
      >
        {/* Logo */}
        <div className={`h-14 flex items-center border-b border-gray-200 flex-shrink-0 ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-base">🍺</span>
          </div>
          {(!collapsed || mobileOpen) && (
            <span className="ml-2.5 text-lg font-bold text-gray-900 truncate">BrewFlow</span>
          )}
        </div>

        {/* Org switcher */}
        {(!collapsed || mobileOpen) && (
          <div className="px-3 py-2 border-b border-gray-100">
            <OrgSwitcher />
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-2 space-y-0.5 ${collapsed && !mobileOpen ? 'px-2' : 'px-3'}`}>
          {navigation.map((entry) => {
            if (isGroup(entry)) {
              return (
                <SidebarGroup
                  key={entry.label}
                  group={entry}
                  isActive={isActive}
                  onNavigate={() => setMobileOpen(false)}
                  collapsed={collapsed && !mobileOpen}
                />
              );
            }

            const Icon = entry.icon;
            const active = isActive(entry.href);

            if (collapsed && !mobileOpen) {
              return (
                <div key={entry.name} className="relative group/nav">
                  <Link
                    to={entry.href}
                    className={`
                      flex items-center justify-center p-2 rounded-lg transition-colors
                      ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}
                    `}
                  >
                    <Icon className="w-5 h-5" />
                  </Link>
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded hidden group-hover/nav:block z-50 whitespace-nowrap">
                    {entry.name}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={entry.name}
                to={entry.href}
                onClick={() => setMobileOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                `}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span>{entry.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle - desktop only */}
        <div className="hidden lg:block border-t border-gray-200 p-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        {/* User section */}
        {user && (
          <div className={`border-t border-gray-200 flex-shrink-0 ${collapsed && !mobileOpen ? 'p-2' : 'p-3'}`}>
            {collapsed && !mobileOpen ? (
              <div className="relative group/user">
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-700">
                    {user.name?.charAt(0) || 'U'}
                  </div>
                </button>
                <div className="absolute left-full bottom-0 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded hidden group-hover/user:block z-50 whitespace-nowrap">
                  {user.name} &middot; Logout
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-semibold text-blue-700 flex-shrink-0">
                  {user.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.role}</p>
                </div>
                <button
                  onClick={logout}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Main area */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile header */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 lg:hidden sticky top-0 z-30 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="ml-3 flex items-center gap-2.5">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-sm">🍺</span>
            </div>
            <span className="text-base font-bold text-gray-900">BrewFlow</span>
          </div>
          {/* Mobile close button when menu is open */}
          {mobileOpen && (
            <button
              onClick={() => setMobileOpen(false)}
              className="ml-auto p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
